import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Setting } from 'app/shared/models/setting.model';
import { SettingsService } from 'app/shared/services/settings.service';
import { PasswordInputType } from 'app/shared/types/password-input-type';
import { SettingType } from 'app/shared/types/setting-type';
import { Utils } from 'app/shared/utils/utils';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-settings-base',
  templateUrl: './settings-base.component.html',
  styleUrls: ['./settings-base.component.scss']
})
export class SettingsBaseComponent implements OnInit {

  @Input() title: string;
  @Input() description: string;
  @Input() details: string;
  @Input() sourceUrl: string;
  @Input() canBeTested: boolean = false;

  dynamicForm: FormGroup;
  settings: Setting[];
  settingType = SettingType;
  passwordIconClass: string = 'ft-eye';
  savedSuccessfullyMessage: string;
  connectionTestedSuccessfullyMessage: string;
  apiKeyCopiedToClipboardMessage: string;
  apiKeyGeneratedMessage: string;

  get formSettings(): FormArray {
    return this.dynamicForm.get('formSettings') as FormArray;
  }

  constructor(protected formBuilder: FormBuilder, protected settingsService: SettingsService, protected toastrService: ToastrService, protected translateService: TranslateService) {
    this.dynamicForm = new FormGroup({
      formSettings: new FormArray([]),
    });
  }

  ngOnInit() {
    this.getSettings();
    this.translateService.get(['settings.savedSuccessfullyMessage', 'settings.connectionTestedSuccessfullyMessage', 'settings.apiKeyCopiedToClipboardMessage', 'settings.apiKeyGeneratedMessage'])
      .subscribe(translations => {
        this.savedSuccessfullyMessage = translations['settings.savedSuccessfullyMessage'];
        this.connectionTestedSuccessfullyMessage = translations['settings.connectionTestedSuccessfullyMessage'];
        this.apiKeyCopiedToClipboardMessage = translations['settings.apiKeyCopiedToClipboardMessage'];
        this.apiKeyGeneratedMessage = translations['settings.apiKeyGeneratedMessage'];
      });
  }

  getSettings() {
    this.settingsService.getCollection<Setting>(this.sourceUrl, null, (collection) => {
      this.settings = collection;
      this.addSettingsToForm();
    });
  }

  addSettingsToForm() {
    this.settings.forEach(setting => {

      var settingform = this.formBuilder.group({
        settingId: [setting.SettingId],
        name: [setting.Name],
        displayName: [setting.DisplayName],
        value: [setting.Value],
        settingTypeCode: [setting.SettingTypeCode],
        passwordType: PasswordInputType.password,
        passwordIconClass: "ft-eye"
      });

      this.formSettings.push(settingform);
    });
  }

  resetForm() {
    this.dynamicForm.reset();
    while (this.formSettings.length !== 0) {
      this.formSettings.removeAt(0)
    }

    this.getSettings();
  }

  togglePassword(index: number, passwordInputType: PasswordInputType) {
    if (passwordInputType == PasswordInputType.password) {
      this.patchPasswordType(index, PasswordInputType.text, "ft-eye-off");
    } else {
      this.patchPasswordType(index, PasswordInputType.password, "ft-eye");
    }
  }

  patchPasswordType(index: number, passwordInputType: PasswordInputType, passwordIconClass: string) {
    this.formSettings.at(index).patchValue({
      passwordType: passwordInputType,
      passwordIconClass: passwordIconClass
    });
  }

  generateApiKey(index: number) {
    this.settingsService.getObject<string>(this.sourceUrl + "/apikey", null, (apiKey) => {
      if (!Utils.isStringNullOrWhiteSpace(apiKey)) {
        this.patchApiKeyValue(index, apiKey);
        this.toastrService.success(this.apiKeyGeneratedMessage);
      }
    });
  }

  patchApiKeyValue(index: number, newApiKey: string) {
    this.formSettings.at(index).patchValue({
      value: newApiKey
    });
  }

  copyApiKey(value: string) {
    Utils.copyToClipBoard(this.toastrService, this.apiKeyCopiedToClipboardMessage, value);
  }

  onSubmit() {
    return this.settingsService.saveSettings<Setting[]>(this.sourceUrl, this.formSettings.value, null, this.savedSuccessfullyMessage);
  }

  test() {
    this.settingsService.testSettings<Setting[]>(this.sourceUrl + "/test", this.formSettings.value, null, this.connectionTestedSuccessfullyMessage);
  }
}
